/**
 * LoginComponent
 *
 * Purpose
 * - Presents the login form and coordinates the authentication flow with AuthenticationService.
 * - On success, stores the bearer token, initializes user/session state, and navigates to /home.
 * - On failure, surfaces a clear message and leaves the user on the login page.
 *
 * Usage
 * - The AuthInterceptor will automatically attach Authorization: Bearer <token> for API calls after login.
 * - This component assumes AuthenticationService.login(...) returns an OAuth-like response.
 *
 * Changes made (robust token handling)
 * - Accept both OAuth response shapes: { access_token } or { token }.
 * - Guard against null/invalid responses to avoid TypeError on data.access_token when the response is null.
 * - Fail fast with a user-friendly message when no token is provided by the server.
 * - Retained the existing session/tab enforcement (window.name + localStorage['rpmsSessionId']).
 *
 * Changes made (login error handling)
 * - The AuthInterceptor now SKIPS error dialogs for /Token requests to prevent duplicates/loops.
 * - This component is responsible for showing the OAuth error_description for login failures.
 * - Extract error_description from the OAuth error response for user-friendly messages.
 *
 * Notes
 * - If your token endpoint ever changes shape, only the token extraction block needs updating.
 * - App-wide auth/cors is handled via a single OWIN Bearer pipeline; the client attaches the token via the interceptor.
 */

import { Component } from '@angular/core';
import { AuthenticationService } from '../shared/authentication.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { PingerService } from '../shared/pinger.service';
import { MessageService } from 'src/app/shared/message.service';
import { UserService } from 'src/app/shared/user.service';
import { PermissionsService } from 'src/app/shared/permissions.service';
import { SignalRService } from 'src/app/shared/signal-r.service';
import { IdleService } from '../shared/idle.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent{

  hide = true;
  isLoginError: boolean = false;
  isLoggedIn: boolean = false;
  isOfflineLinkVisible;

  // Basic required validators for username/password
  loginForm = this._fb.group({
    userName: ['', Validators.required],
    password: ['', Validators.required],
  });

  constructor(
    private _authenticationService: AuthenticationService,
    private _permissionsService: PermissionsService,
    private _userService: UserService,
    private _router : Router, 
    private _fb: UntypedFormBuilder,
    private _pingerService: PingerService,
    private _idleService: IdleService,
    private _messageService: MessageService,
    private _signalRService: SignalRService,
  ) { }

  ngOnInit(){
    //this.getStoreDataFromIndexedDb();
    this.createIndexedDbIfMissing();
  }

  ngAfterContentInit(){
    this.isLoggedIn = false;
  }

  onSubmit(){
    // Avoid submitting an invalid form
    if (this.loginForm.invalid) {
      this._messageService.onFailure('Please enter both username and password.', { status: 400 });
      return;
    }

    this._authenticationService.login(this.loginForm.value)
      .subscribe(
        (data : any)=>{
          // Accept both shapes from the token endpoint: { access_token } or { token }
          const token = data && (data.access_token || data.token);

          // Guard: if no token provided, surface a friendly message and stop
          if (!token || typeof token !== 'string' || token.trim().length === 0) {
            this.isLoginError = true;
            this._messageService.onFailure('Login failed. No token was returned by the server.', data);
            return;
          }

          // Persist token and mark session as logged in
          this.isLoggedIn = true;
          this._authenticationService.setToken(token);
          this._authenticationService.setIsUserLoggedIn(true);

          // Set unique string on window.name to keep user from using multiple tabs
          // The check for this is in auth.guard
          const id = new Date().getTime().toString();
          window.name = id;

          // Don't store it in localStorage if logging in and someone is already logged in on another tab
          const sessionId = localStorage.getItem('rpmsSessionId');
          if (!sessionId) localStorage.setItem('rpmsSessionId', id);

          // Continue with post-login initialization (user, permissions, timers, SignalR, navigation)
          this.getMyUserFromServer();
        },
        (response : HttpErrorResponse)=>{
          // Set local error state for UI purposes (e.g., form styling, error indicators)
          this.isLoginError = true;
          
          // Show the error to the user.
          // The AuthInterceptor SKIPS error dialogs for /Token requests to prevent loops,
          // so we must handle showing the error here.
          // Extract OAuth error_description if available, otherwise show a generic message.
          const errorDescription = response.error?.error_description;
          if (errorDescription) {
            this._messageService.onFailure('Login failed.', response);
          } else {
            this._messageService.onFailure('Login failed. Please check your credentials and try again.', response);
          }
        });
  }

  getMyUserFromServer(){
    this._userService.getMyUser()
      .subscribe(
        (data : any)=>{

          // Block UI access for specific roles
          if (data.roles[0] === 'Mainframe' || data.roles[0] === 'Automation'){
            this._messageService.alert('Users in a Mainframe or Automation role do not have access to this user interface.');
            this._authenticationService.logout();
          }
          else{
            // Initialize session state and navigate to /home
            this._authenticationService.setMyUser(data);
            this._permissionsService.initRolePermissions(data.roles[0]);
            this._idleService.resetIdleWatch();
            this._pingerService.startPingerTimer();
            this._signalRService.loadSignalRHubs();
            this._router.navigate(['/home']);
          }
        },
        (response : HttpErrorResponse)=>{

          this._messageService.onFailure('Failed to get user.', response);
        });
  }

  createIndexedDbIfMissing() {
    var self = this;
    var db;
    var stores = [];
    var storeIdsToDelete = [];
    var requestOpen = window.indexedDB.open("RPMS2", 3);

    requestOpen.onerror = function (event) {
      console.warn("Failed to open offline database.");
    };

    requestOpen.onsuccess = function (event:any) {
      db = event.target.result;

      var transaction = db.transaction(["pricingData"], "readwrite");
      var objectStore = transaction.objectStore("pricingData");

      ///////////////////  Can remove this code in June 2019.  This is to remove IDB store pricing data in the old key format  (i.e. 16, not 16-1 and 16-2)

      objectStore.openCursor().onsuccess = function (event) {
        var cursor = event.target.result;

        if (cursor) {
          if (!cursor.value.key || String(cursor.value.key).indexOf('-') == -1) {
            //delete this one
            storeIdsToDelete.push(cursor.value.storeId);
            var request = cursor.delete();
          }
          cursor.continue();
        }
        else {
          // no more entries
          transaction = db.transaction(["storeData"], "readwrite");
          objectStore = transaction.objectStore("storeData");

          objectStore.openCursor().onsuccess = function (event) {
            var cursor = event.target.result;

            if (cursor) {
              if (storeIdsToDelete.indexOf(cursor.value.storeId) > -1) {
                var request = cursor.delete();
              }
              else {
                stores.push(cursor.value.storeId);
              }
              cursor.continue();
            }
            else {
              //no more storeData entries
            }
          };
        }
      };

    };

    requestOpen.onupgradeneeded = function (event:any) {
      var newVersion = event.target.result;
      //remove all object stores
      if (newVersion.objectStoreNames.contains("pricingData")) newVersion.deleteObjectStore("pricingData");
      if (newVersion.objectStoreNames.contains("storeData")) newVersion.deleteObjectStore("storeData");
      //if (newVersion.objectStoreNames.contains("columnMetadata")) newVersion.deleteObjectStore("columnMetadata");

      //then add all object stores
      if (!newVersion.objectStoreNames.contains("pricingData")) newVersion.createObjectStore("pricingData", { keyPath: "key" });
      if (!newVersion.objectStoreNames.contains("storeData")) newVersion.createObjectStore("storeData", { keyPath: "storeId" });
      //if (!newVersion.objectStoreNames.contains("columnMetadata")) newVersion.createObjectStore("columnMetadata", { keyPath: "id" });

    };

  }

  navigateToOffline(){
    this._router.navigate(['/offline-price-management']);
  }
}
